import moment from "moment-mini";

import ApiHelper from "./ApiHelper";

export const RANGE_FILTER_DISPLAY_FORMAT = "DD.MM.YYYY";
export const NUMBER_PERCENT_VALUES_FORMAT = "number_percent";

const RANGE_FILTER_DATE_FORMATS = [
  "DD.MM.YYYY",
  "YYYY-MM-DDTHH:mm:ss.SSS",
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD"
];

export function isPercentRangeFilter(column) {
  return column?.values_format === NUMBER_PERCENT_VALUES_FORMAT;
}

export function formatRangeFilterNumberForDisplay(value, column) {
  if (value === null || value === undefined || value === "") {
    return value;
  }

  if (!isPercentRangeFilter(column)) {
    return value;
  }

  return `${value}%`;
}

export function normalizeRangeFilterDate(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = moment(String(value).trim(), RANGE_FILTER_DATE_FORMATS, true);

  if (!parsed.isValid()) {
    return null;
  }

  return parsed.format("YYYY-MM-DD");
}

export function formatRangeFilterDateForDisplay(value) {
  const normalized = normalizeRangeFilterDate(value);

  if (!normalized) {
    return null;
  }

  return moment(normalized, "YYYY-MM-DD", true).format(RANGE_FILTER_DISPLAY_FORMAT);
}

export function getAllowedRangeDateBounds(column) {
  const min = normalizeRangeFilterDate(column?.filter_data?.min);
  const max = normalizeRangeFilterDate(column?.filter_data?.max);

  if (!min || !max) {
    return null;
  }

  return { min, max };
}

export function isDateWithinAllowedRange(value, column) {
  const parsed = normalizeRangeFilterDate(value);

  if (!parsed) {
    return false;
  }

  const allowed = getAllowedRangeDateBounds(column);

  if (!allowed) {
    return true;
  }

  return parsed >= allowed.min && parsed <= allowed.max;
}

export function hasDateOutOfAllowedRange(filterValue, column) {
  return ["min", "max"].some(field => {
    const bound = filterValue?.[field];

    if (bound === null || bound === undefined || bound === "") {
      return false;
    }

    if (normalizeRangeFilterDate(bound) === null) {
      return false;
    }

    return !isDateWithinAllowedRange(bound, column);
  });
}

export function getDateOutOfAllowedRangeMessage(column, translate) {
  const allowed = getAllowedRangeDateBounds(column);

  if (!allowed) {
    return translate("range_filter_date_out_of_allowed_bounds");
  }

  return translate("range_filter_date_out_of_allowed_bounds", {
    min: formatRangeFilterDateForDisplay(allowed.min),
    max: formatRangeFilterDateForDisplay(allowed.max)
  });
}

export function getRangeFilterBound(
  filterValue,
  field,
  { isDateRange = false, column = null } = {}
) {
  const value = filterValue?.[field];

  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (isDateRange) {
    const normalized = normalizeRangeFilterDate(value);

    if (!normalized) {
      return null;
    }

    if (column && !isDateWithinAllowedRange(value, column)) {
      return null;
    }

    return normalized;
  }

  return value;
}

export function hasActiveRangeFilterBounds(
  filterValue,
  { isDateRange = false, column = null } = {}
) {
  return (
    getRangeFilterBound(filterValue, "min", { isDateRange, column }) !== null ||
    getRangeFilterBound(filterValue, "max", { isDateRange, column }) !== null
  );
}

export function formatRangeFilterDisplayLabel({
  filterValue,
  columnData,
  translate,
  formatValue
}) {
  const resolveFormatValue =
    typeof formatValue === "function"
      ? formatValue
      : value => formatRangeFilterNumberForDisplay(value, columnData);
  const isDateRange = ApiHelper.getIsDateRange(columnData);
  const min = getRangeFilterBound(filterValue, "min", {
    isDateRange,
    column: columnData
  });
  const max = getRangeFilterBound(filterValue, "max", {
    isDateRange,
    column: columnData
  });

  if (min == null && max == null) {
    return null;
  }

  if (isDateRange) {
    const parts = [];
    const formatDate = value =>
      formatRangeFilterDateForDisplay(value) ?? resolveFormatValue(value);

    if (min != null) {
      parts.push(`${translate("From")} ${formatDate(min)}`);
    }

    if (max != null) {
      parts.push(`${translate("To")} ${formatDate(max)}`);
    }

    return parts.join(" ");
  }

  if (min != null && max != null) {
    return `${resolveFormatValue(min)} - ${resolveFormatValue(max)}`;
  }

  if (min != null) {
    return resolveFormatValue(min);
  }

  return resolveFormatValue(max);
}

export function hasRangeFilterBound(value, isDateRange, column = null) {
  if (value === null || value === undefined || value === "") {
    return false;
  }

  if (isDateRange) {
    return (
      normalizeRangeFilterDate(value) !== null &&
      (!column || isDateWithinAllowedRange(value, column))
    );
  }

  return !isNaN(value) || !isNaN(String(value).replace(",", "."));
}

export function hasInvalidDateBounds(filterValue) {
  return ["min", "max"].some(field => {
    const bound = filterValue?.[field];

    if (bound === null || bound === undefined || bound === "") {
      return false;
    }

    return normalizeRangeFilterDate(bound) === null;
  });
}

export function hasInvalidNumericBounds(filterValue) {
  return ["min", "max"].some(field => {
    const bound = filterValue?.[field];

    if (bound === null || bound === undefined || bound === "") {
      return false;
    }

    return (
      isNaN(bound) && isNaN(String(bound).replace(",", "."))
    );
  });
}

export function hasInvalidRangeOrder(filterValue, column, isDateRange) {
  if (!filterValue) {
    return false;
  }

  if (isDateRange && hasInvalidDateBounds(filterValue)) {
    return false;
  }

  if (isDateRange && hasDateOutOfAllowedRange(filterValue, column)) {
    return false;
  }

  let min;
  let max;

  if (isDateRange) {
    min = getRangeFilterBound(filterValue, "min", {
      isDateRange: true,
      column
    });
    max = getRangeFilterBound(filterValue, "max", {
      isDateRange: true,
      column
    });
  } else {
    min =
      filterValue.min !== null &&
      filterValue.min !== undefined &&
      filterValue.min !== ""
        ? Number(String(filterValue.min).replace(",", "."))
        : column.filter_data.min;
    max =
      filterValue.max !== null &&
      filterValue.max !== undefined &&
      filterValue.max !== ""
        ? Number(String(filterValue.max).replace(",", "."))
        : column.filter_data.max;
  }

  return min > max;
}

export function hasInvalidRangeFilterSettings(filters, columns) {
  if (!filters || !columns?.length) {
    return false;
  }

  return columns.some(column => {
    if (!ApiHelper.getIsFilterableByRange(column)) {
      return false;
    }

    const field = ApiHelper.getColumnAttributeForFiltering(column);
    const filterValue = filters[field];

    if (!filterValue || typeof filterValue.min === "undefined") {
      return false;
    }

    const isDateRange = ApiHelper.getIsDateRange(column);

    if (isDateRange && hasInvalidDateBounds(filterValue)) {
      return true;
    }

    if (isDateRange && hasDateOutOfAllowedRange(filterValue, column)) {
      return true;
    }

    if (!isDateRange && hasInvalidNumericBounds(filterValue)) {
      return true;
    }

    return hasInvalidRangeOrder(filterValue, column, isDateRange);
  });
}
